import { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import {
  Monitor,
  Tablet,
  Smartphone,
  ArrowLeft,
  Rocket,
} from "lucide-react";
import { DocumentRenderer, type Breakpoint } from "@zirro/renderer";
import type { SiteDocument, SiteNode, Href } from "@zirro/schema";
import { useTemplate } from "../../lib/queries";
import { subcategoryLabel } from "../../lib/taxonomy";
import { Badge } from "../../components/ui/Badge";
import { Spinner } from "../../components/ui/Spinner";
import { AppLayout } from "../../components/layout/AppLayout";
import { StorefrontProvider, hasCatalogSlot } from "../public/store/StorefrontContext";
import { renderStoreSlot } from "../public/store/slots";
import { HeaderActions } from "../public/store/Cart";
import { Catalog } from "../public/store/Catalog";
import { type StoreRoute, storeRouteFor, renderStoreRoute } from "../public/store/routes";
import { getSampleStoreForTemplate } from "./sampleStore";
import "../public/store/store.css";

const DEVICES: { id: Breakpoint; label: string; width: number; icon: typeof Monitor }[] = [
  { id: "desktop", label: "Desktop", width: 1280, icon: Monitor },
  { id: "tablet", label: "Tablet", width: 768, icon: Tablet },
  { id: "mobile", label: "Mobile", width: 375, icon: Smartphone },
];

/**
 * Template preview with a device switcher and interactive page navigation:
 * Renders the sample-substituted document at desktop/tablet/mobile widths.
 * Header menu links, buttons, footer links and product items are interactive
 * and navigate cleanly between the template's pages.
 */
export function TemplatePreviewPage() {
  const { templateId } = useParams();
  const { data: detail, isPending, error } = useTemplate(templateId);
  const [device, setDevice] = useState<Breakpoint>("desktop");
  const [activePageId, setActivePageId] = useState<string | undefined>(undefined);
  const [activeStoreRoute, setActiveStoreRoute] = useState<StoreRoute | null>(null);
  const galleryHref = useLocation().pathname.startsWith("/app") ? "/app/templates" : "/templates";

  const docPages = useMemo(() => {
    return (detail?.document?.pages as any[]) || [];
  }, [detail]);

  const isStoreTemplate = useMemo(() => {
    if (!detail) return false;
    if (detail.kind === "store") return true;
    if (Boolean((detail as any).storeDefaults)) return true;
    if (detail.name.toLowerCase().includes("store")) return true;
    if (docPages.some((p: any) => p.slug === "/shop" || hasCatalogSlot(p.nodes || []))) return true;
    return false;
  }, [detail, docPages]);

  const sampleStore = useMemo(() => {
    if (!detail || !isStoreTemplate) return null;
    return getSampleStoreForTemplate(detail);
  }, [detail, isStoreTemplate]);

  const activePage = useMemo(() => {
    if (!docPages.length) return undefined;
    return docPages.find((p) => p.id === activePageId) ?? docPages[0];
  }, [docPages, activePageId]);

  const handleNavigate = (href: Href) => {
    setActiveStoreRoute(null);
    if (!docPages.length) return;

    if (href.type === "page" && href.pageId) {
      const target = docPages.find((p) => p.id === href.pageId);
      if (target) {
        setActivePageId(target.id);
        const frame = document.querySelector("[data-testid='preview-frame']");
        frame?.scrollTo?.({ top: 0, behavior: "smooth" });
        window?.scrollTo?.({ top: 0, behavior: "smooth" });
      }
    } else if (href.type === "url" && href.url) {
      if (href.url === "/" || href.url === "") {
        const home = docPages.find((p) => p.slug === "/") ?? docPages[0];
        if (home) setActivePageId(home.id);
        window?.scrollTo?.({ top: 0, behavior: "smooth" });
      } else if (href.url.startsWith("/")) {
        const matching = docPages.find((p) => p.slug === href.url || p.slug === href.url.replace(/\/$/, ""));
        if (matching) {
          setActivePageId(matching.id);
          window?.scrollTo?.({ top: 0, behavior: "smooth" });
        } else if (href.url.includes("/shop")) {
          const shop = docPages.find((p) => p.slug === "/shop");
          if (shop) setActivePageId(shop.id);
          window?.scrollTo?.({ top: 0, behavior: "smooth" });
        }
      } else if (href.url.startsWith("#")) {
        const el = document.getElementById(href.url.slice(1));
        el?.scrollIntoView?.({ behavior: "smooth" });
      } else if (href.url.startsWith("http://") || href.url.startsWith("https://") || href.url.startsWith("mailto:") || href.url.startsWith("tel:")) {
        window.open(href.url, "_blank", "noopener,noreferrer");
      }
    }
  };

  const handleStoreGo = (path: string) => {
    const route = storeRouteFor(path);
    if (route) {
      setActiveStoreRoute(route);
      const frame = document.querySelector("[data-testid='preview-frame']");
      frame?.scrollTo?.({ top: 0, behavior: "smooth" });
      window?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }
    setActiveStoreRoute(null);
    if (path.includes("/shop")) {
      const shop = docPages.find((p) => p.slug === "/shop");
      if (shop) setActivePageId(shop.id);
    } else {
      const home = docPages.find((p) => p.slug === "/") ?? docPages[0];
      if (home) setActivePageId(home.id);
    }
    window?.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  if (isPending) {
    return (
      <AppLayout>
        <main className="page page--wide">
          <div className="loading-center" style={{ padding: "var(--z-space-16)" }}>
            <Spinner size="lg" />
            <p className="text-muted">Loading preview…</p>
          </div>
        </main>
      </AppLayout>
    );
  }

  if (error || !detail) {
    return (
      <AppLayout>
        <main className="page page--wide" style={{ textAlign: "center", paddingTop: "var(--z-space-24)" }}>
          <h1>Template not found</h1>
          <p className="text-muted" style={{ marginBottom: "var(--z-space-6)" }}>
            It may have been removed from the catalog.
          </p>
          <Link className="btn btn--secondary" to={galleryHref}>
            <ArrowLeft size={16} />
            Back to templates
          </Link>
        </main>
      </AppLayout>
    );
  }

  const active = DEVICES.find((entry) => entry.id === device) ?? DEVICES[0];
  const appendCatalog = isStoreTemplate && activePage?.slug === "/shop" && !hasCatalogSlot(activePage.nodes || []);

  return (
    <AppLayout>
      <main className="page page--wide preview-page">
        <header className="page-header">
          <div>
            <div style={{ marginBottom: "var(--z-space-2)" }}>
              <Badge variant="primary">{detail.kind === "store" ? "Online store" : subcategoryLabel(detail.subcategory)}</Badge>
            </div>
            <h1>{detail.name}</h1>
            <p className="text-muted" style={{ marginTop: "var(--z-space-1)" }}>
              {detail.description}
            </p>
          </div>
          <div className="preview-page__actions">
            <Link className="btn btn--ghost" to={galleryHref}>
              <ArrowLeft size={16} />
              All templates
            </Link>
            <Link
              className="btn btn--primary"
              to={`/app/onboarding?template=${detail.id}`}
            >
              <Rocket size={16} />
              Use this template
            </Link>
          </div>
        </header>

        {/* Device & Page navigation switcher controls */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "var(--z-space-6)",
          }}
        >
          {/* Device switcher */}
          <div
            className="device-switch"
            role="group"
            aria-label="Preview device"
            style={{
              display: "inline-flex",
              gap: "4px",
              padding: "4px",
              background: "var(--z-gray-100)",
              borderRadius: "var(--z-radius-lg)",
            }}
          >
            {DEVICES.map((entry) => {
              const Icon = entry.icon;
              return (
                <button
                  key={entry.id}
                  type="button"
                  className={`btn btn--sm ${device === entry.id ? "btn--primary" : "btn--ghost"}`}
                  aria-pressed={device === entry.id}
                  onClick={() => setDevice(entry.id)}
                >
                  <Icon size={14} />
                  {entry.label} · {entry.width}px
                </button>
              );
            })}
          </div>

          {/* Template Page tabs */}
          {docPages.length > 1 && (
            <div
              className="preview-page-tabs"
              role="tablist"
              aria-label="Template pages"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px",
                background: "var(--z-gray-100)",
                borderRadius: "var(--z-radius-lg)",
              }}
            >
              <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--z-gray-500)", padding: "0 8px" }}>
                Pages:
              </span>
              {docPages.map((p: any) => {
                const isCurrent = activePage?.id === p.id && !activeStoreRoute;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="tab"
                    aria-selected={isCurrent}
                    className={`btn btn--sm ${isCurrent ? "btn--primary" : "btn--ghost"}`}
                    onClick={() => {
                      setActiveStoreRoute(null);
                      setActivePageId(p.id);
                    }}
                  >
                    {p.navLabel || p.title || p.slug}
                  </button>
                );
              })}
              {activeStoreRoute ? (
                <button
                  type="button"
                  className="btn btn--sm btn--primary"
                  onClick={() => setActiveStoreRoute(null)}
                  title="Back to page"
                >
                  Product view ✕
                </button>
              ) : null}
            </div>
          )}
        </div>

        <div className="preview-viewport" style={{ display: "flex", justifyContent: "center", overflowX: "auto" }}>
          <div
            className="preview-frame"
            style={{
              width: active.width,
              maxWidth: "100%",
              transition: "width 0.3s ease, border-radius 0.3s ease",
              boxShadow: "0 20px 40px -15px rgba(0,0,0,0.1)",
              borderRadius: device === "mobile" ? "20px" : "12px",
              border: device === "mobile" ? "6px solid #1e293b" : "1px solid var(--z-border, #e2e8f0)",
            }}
            data-testid="preview-frame"
          >
            {isStoreTemplate && sampleStore ? (
              <StorefrontProvider
                store={sampleStore}
                interactive={true}
                basePath={`/templates/${detail.id}`}
                onGo={handleStoreGo}
              >
                <DocumentRenderer
                  document={detail.document as unknown as SiteDocument}
                  pageId={activePage?.id}
                  activePage={activeStoreRoute ? { id: "store-route-page", slug: "", title: "", nodes: [] } : undefined}
                  mode="preview"
                  breakpoint={device}
                  onNavigate={handleNavigate}
                  renderSlot={renderStoreSlot}
                  headerActions={<HeaderActions />}
                  pageAddon={
                    activeStoreRoute ? (
                      renderStoreRoute(activeStoreRoute)
                    ) : appendCatalog ? (
                      <div className="zs-page">
                        <Catalog />
                      </div>
                    ) : undefined
                  }
                />
              </StorefrontProvider>
            ) : (
              <DocumentRenderer
                document={detail.document as unknown as SiteDocument}
                pageId={activePage?.id}
                mode="preview"
                breakpoint={device}
                onNavigate={handleNavigate}
              />
            )}
          </div>
        </div>
      </main>
    </AppLayout>
  );
}
